import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { UilPlus, UilTimes } from "@iconscout/react-unicons";
import { useNavigate } from "react-router-dom";
import AddMovieHeader from "../components/AddMovieHeader";
import PageHeader from "../components/Header";
import { toast } from "react-toastify";
import useMutation from "hooks/useMutation";


// ================== Component Start ==================
export default function CastCrew({ onCancel, details }) {

  // ======= Form and State Setup =======
  const methods = useForm({
    defaultValues: {
      castMembers: details?.cast || [],
      crewMembers: details?.crew || [],
    },
  });
  const { handleSubmit, setValue, watch, getValues } = methods;
  const navigate = useNavigate();

  const [castActorName, setCastActorName] = useState("");
  const [castCharacterName, setCastCharacterName] = useState("");
  const [crewName, setCrewName] = useState("");
  const [crewRole, setCrewRole] = useState("");
  const { mutate, loading } = useMutation({ method: 'post', url: `/entertainment/movie-box/medias/${details?.id}/cast-and-crew` })

  // ======= Handlers for Cast Members =======
  const handleAddCast = () => {
    if (castActorName.trim() && castCharacterName.trim()) {
      const updated = [
        ...(getValues("castMembers") || []),
        { name: castActorName.trim(), character: castCharacterName.trim() },
      ];
      setValue("castMembers", updated);
      setCastActorName("");
      setCastCharacterName("");
    }
  };

  const handleRemoveCast = (idx) => {
    const updated = (getValues("castMembers") || []).filter((_, i) => i !== idx);
    setValue("castMembers", updated);
  };

  // ======= Handlers for Crew Members =======
  const handleAddCrew = () => {
    if (crewName.trim() && crewRole.trim()) {
      const updated = [
        ...(getValues("crewMembers") || []),
        { name: crewName.trim(), role: crewRole.trim() },
      ];
      setValue("crewMembers", updated);
      setCrewName("");
      setCrewRole("");
    }
  };

  const handleRemoveCrew = (idx) => {
    const updated = (getValues("crewMembers") || []).filter((_, i) => i !== idx);
    setValue("crewMembers", updated);
  };

  // ======= Cancel Handler =======
  const handleCancel = () => {
    if (typeof onCancel === "function") onCancel();
    navigate(-1);
  };

  // ======= Form Submit Handler =======
  const onSubmit = (data) => {
    const cast = data?.castMembers || [];
    const crew = data?.crewMembers || [];

    // Block if either list is empty
    if (!cast.length || !crew.length) {
      toast.error("Please add at least one cast and one crew member.");
      return;
    }

    // If both lists exist and no changes from initial details, skip API and proceed
    const initialCast = details?.cast || [];
    const initialCrew = details?.crew || [];

    const normalize = (arr, keys) =>
      (arr || [])
        .map((o) => keys.reduce((acc, k) => {
          acc[k] = (o?.[k] || "").trim();
          return acc;
        }, {}))
        .sort((a, b) => {
          for (const k of keys) {
            const cmp = (a[k] || "").localeCompare(b[k] || "");
            if (cmp !== 0) return cmp;
          }
          return 0;
        });

    const castUnchanged = JSON.stringify(normalize(initialCast, ["name", "character"])) === JSON.stringify(normalize(cast, ["name", "character"]));
    const crewUnchanged = JSON.stringify(normalize(initialCrew, ["name", "role"])) === JSON.stringify(normalize(crew, ["name", "role"]));

    if (initialCast.length && initialCrew.length && castUnchanged && crewUnchanged) {
      // No new or changed entries; proceed without saving
      navigate("/movie-box/add-movie/movie-artwork" + (details?.id ? `?id=${details.id}` : ""));
      return;
    }

    // Otherwise, save changes
    mutate({
      payload: {
        media_id: details?.id,
        crew: data?.crewMembers,
        cast: data?.castMembers,
      },
      onSuccess() {
        toast.success("Cast & Crew details saved successfully");
        navigate("/movie-box/add-movie/movie-artwork" + (details?.id ? `?id=${details.id}` : ""));
      },
      onError(err) {
        toast.error(err?.message || "Could not save cast & crew details. Please try again.");
      },
    });
  };

  // ================== Render ==================
  return (
    <FormProvider {...methods}>
      <div className="min-h-screen flex flex-col bg-gray-50">
        {/* ======= Header Section ======= */}
        <AddMovieHeader
          onCancel={handleCancel}
          showProgress={true}
          currentStep={2}
          onContinue={handleSubmit(onSubmit)}
          continueDisabled={!(watch("castMembers")?.length && watch("crewMembers")?.length)}
          loading={loading}
        />

        <main className="flex-1 flex justify-center px-4 py-8">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full max-w-2xl bg-white p-6 rounded-md shadow-sm"
          >
            {/* ======= Page Title and Description ======= */}
            <PageHeader
              title="Cast & Crew Details"
              description="List the main actors and the key contributors featured in your movie."
            />

            {/* ======= Cast Members Section ======= */}
            <div className="shadow-sm border rounded-md p-5 mb-8">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Cast Members
              </h2>

              {/* Add Cast Member Input */}
              <div className="flex gap-2 mb-3 flex-wrap">
                <input
                  type="text"
                  value={castActorName}
                  onChange={(e) => setCastActorName(e.target.value)}
                  placeholder="Actor name"
                  className="flex-1 border rounded-md px-3 py-2 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCast();
                    }
                  }}
                />
                <input
                  type="text"
                  value={castCharacterName}
                  onChange={(e) => setCastCharacterName(e.target.value)}
                  placeholder="Character name"
                  className="flex-1 border rounded-md px-3 py-2 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCast();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddCast}
                  className="inline-flex items-center px-3 py-2 bg-white border text-sm font-medium rounded-md hover:bg-gray-100 text-black"
                  disabled={!castActorName.trim() || !castCharacterName.trim()}
                >
                  <UilPlus className="h-4 w-4 mr-1" />
                  Add
                </button>
              </div>

              {/* List of Cast Members */}
              <div className="flex flex-wrap gap-2">
                {(watch("castMembers") || []).map((member, idx) => (
                  <div
                    key={idx}
                    className="flex items-center bg-gray-100 text-sm px-3 py-1 rounded-full"
                  >
                    <span>
                      {member.name} <span className="text-gray-500">as</span> {member.character}
                    </span>
                    <UilTimes
                      className="ml-2 h-4 w-4 cursor-pointer text-gray-500 hover:text-red-500"
                      onClick={() => handleRemoveCast(idx)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* ======= Crew Members Section ======= */}
            <div className="shadow-sm border rounded-md p-5 mb-8">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Crew Members
              </h2>
              {/* Add Crew Member Input */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={crewName}
                  onChange={(e) => setCrewName(e.target.value)}
                  placeholder="Crew member name"
                  className="flex-1 border rounded-md px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  value={crewRole}
                  onChange={(e) => setCrewRole(e.target.value)}
                  placeholder="Role (e.g. Producer)"
                  className="flex-1 border rounded-md px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddCrew}
                  className="inline-flex items-center px-3 py-2 bg-white border text-sm font-medium rounded-md hover:bg-gray-100 text-black"
                  disabled={!crewName.trim() || !crewRole.trim()}
                >
                  <UilPlus className="h-4 w-4 mr-1" />
                  Add Crew
                </button>
              </div>
              {/* List of Crew Members */}
              <div className="flex flex-wrap gap-2">
                {(watch("crewMembers") || []).map((member, idx) => (
                  <div
                    key={idx}
                    className="flex items-center bg-gray-100 text-sm px-3 py-1 rounded-full"
                  >
                    <span>
                      {member.name} <span className="text-gray-500">({member.role})</span>
                    </span>
                    <UilTimes
                      className="ml-2 h-4 w-4 cursor-pointer text-gray-500 hover:text-red-500"
                      onClick={() => handleRemoveCrew(idx)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </form>
        </main>
      </div>
    </FormProvider>
  );
}
// ================== Component End ==================
